import { useContext, useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import LockOutlined from '@mui/icons-material/LockOutlined';
import UserAuthService from '../../../shared/services/user-auth/user-auth.service';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import { AuthLayout } from './auth/auth-layout';
import { AuthField } from './auth/auth-field';
import { PasswordAdornment } from './auth/password-adornment';
import { useAuthBranding } from './auth/use-auth-branding';
import { AUTH_TOKENS, BRAND_DISPLAY_FONT } from './auth/auth-branding';

export const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const userAuthService = new UserAuthService();
  const userNotificationsContext = useContext(UserNotificationsContext);
  const branding = useAuthBranding();

  const [validToken, setValidToken] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const resetPasswordSchema = Yup.object().shape({
    password: Yup.string().min(8, 'Your password must be at least 8 characters').required('Password is required'),
    passwordConfirmation: Yup.string().oneOf([Yup.ref('password')], 'Passwords must match').required('You must confirm your password'),
  });

  const formik = useFormik({
    initialValues: {
      token: '',
      password: '',
      passwordConfirmation: '',
    },
    validationSchema: resetPasswordSchema,
    onSubmit: (values) => {
      return userAuthService
        .resetPassword(values)
        .then(() => {
          navigate('/login');
          userNotificationsContext.openSnackbar({ open: true, message: 'Updated successfully' });
        })
        .catch((err) => {
          userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
          console.log(err);
        });
    },
  });

  // Validate the reset token; bounce back to /forgot-password if it is missing or expired.
  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      userAuthService
        .checkUserToken(token)
        .then(() => {
          setValidToken(true);
          formik.setFieldValue('token', token);
        })
        .catch(() => {
          navigate('/forgot-password');
        });
    } else {
      navigate('/forgot-password');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!validToken) {
    return (
      <AuthLayout branding={branding}>
        <Stack alignItems="center" justifyContent="center" spacing={2} sx={{ minHeight: 200 }}>
          <CircularProgress size={28} sx={{ color: 'primary.main' }} />
          <Typography sx={{ fontSize: '0.9375rem', color: AUTH_TOKENS.inkSecondary }}>Checking your reset link…</Typography>
        </Stack>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout branding={branding}>
      <Box component="form" onSubmit={formik.handleSubmit} noValidate sx={{ width: '100%' }}>
        <Typography
          component="h1"
          sx={{ mb: '6px', fontSize: '1.6rem', fontFamily: BRAND_DISPLAY_FONT, letterSpacing: '-0.015em', color: 'text.primary' }}
        >
          Set a new password
        </Typography>
        <Typography sx={{ mb: '26px', fontSize: '0.9375rem', lineHeight: 1.5, color: AUTH_TOKENS.inkSecondary }}>
          Choose a new password for your account.
        </Typography>

        <Stack spacing={2}>
          <AuthField
            id="password"
            name="password"
            label="New password"
            type={showPassword ? 'text' : 'password'}
            placeholder="At least 8 characters"
            autoComplete="new-password"
            icon={<LockOutlined />}
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.password && Boolean(formik.errors.password)}
            helperText={formik.touched.password ? formik.errors.password : undefined}
            focusRing={branding.focusRing}
            endAdornment={<PasswordAdornment visible={showPassword} onToggle={() => setShowPassword((v) => !v)} />}
          />

          <AuthField
            id="passwordConfirmation"
            name="passwordConfirmation"
            label="Confirm new password"
            type={showConfirm ? 'text' : 'password'}
            placeholder="Re-enter your password"
            autoComplete="new-password"
            icon={<LockOutlined />}
            value={formik.values.passwordConfirmation}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.passwordConfirmation && Boolean(formik.errors.passwordConfirmation)}
            helperText={formik.touched.passwordConfirmation ? formik.errors.passwordConfirmation : undefined}
            focusRing={branding.focusRing}
            endAdornment={<PasswordAdornment visible={showConfirm} onToggle={() => setShowConfirm((v) => !v)} />}
          />
        </Stack>

        <Button
          type="submit"
          fullWidth
          size="large"
          disabled={formik.isSubmitting}
          sx={{ mt: '24px', height: 50, fontSize: '0.9375rem' }}
        >
          {formik.isSubmitting ? <CircularProgress size={20} color="inherit" /> : 'Reset password'}
        </Button>
      </Box>
    </AuthLayout>
  );
};
